import { USE_AI_AGENTS_COLUMN_MAPPING } from "../../../flags";
import * as staticDataService from "../../../services/staticDataService";
import { useGetColumnMappingSuggestionsQuery } from "../../../services/suggestionService";
import {
  useGetColumnTypeSummaryQuery,
  useGetHeaderRowQuery,
  useGetTablePageQuery,
} from "../../../services/tablesService";
import CentredLoader from "./CentredLoader";
import {
  EditRounded as EditIcon,
  CheckRounded as ActiveIcon,
} from "@mui/icons-material";
import {
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  Fade,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as numbers from "common/numbers";
import Button from "components/common/Button";
import DefaultLoader from "components/common/DefaultLoader";
import InlineSearch from "components/common/InlineSearch";
import { Fragment, useEffect, useState, useMemo } from "react";
import { useGetColumnMappingSuggestionsAIQuery } from "services/aiAgents";
import * as utils from "utils";

const HeaderAction = ({
  mappedColumn,
  requiredMissing,
  tip,
  isLoading,
  onClick,
}) => {
  if (isLoading) {
    return (
      <DefaultLoader
        type={"TailSpin"}
        size={18}
        style={{ marginRight: "6px", fontSize: "18px", lineHeight: "12px" }}
      />
    );
  }

  if (mappedColumn) {
    return (
      <IconButton onClick={onClick}>
        <EditIcon />
      </IconButton>
    );
  }

  const title = requiredMissing
    ? "This column is required"
    : tip || "This column is optional";
  const icon = requiredMissing ? <EditIcon /> : <EditIcon />;

  return (
    <Tooltip title={title} arrow>
      <IconButton onClick={onClick}>{icon}</IconButton>
    </Tooltip>
  );
};

const LabelledValue = ({ label, value, margin }) => (
  <Typography variant={"caption"} display={"block"} marginBottom={margin}>
    <b>{label}</b>
    {value}
  </Typography>
);

const ColumnLabel = ({ index, margin }) => (
  <LabelledValue
    label={`Column ${numbers.numberToAlphabetical(index)}`}
    margin={margin ?? 1}
  />
);

const NumberSummary = ({ min, max, columnIndex }) => (
  <>
    <ColumnLabel index={columnIndex} />
    <LabelledValue label={"Min: "} value={min} />
    <LabelledValue label={"Max: "} value={max} />
  </>
);

const DateSummary = ({ min, max, columnIndex }) => (
  <>
    <ColumnLabel index={columnIndex} />
    <LabelledValue label={"From: "} value={min} />
    <LabelledValue label={"To: "} value={max} />
  </>
);

const StringValueCount = ({ value, count, tooltip }) => {
  let label = value || "[BLANK]";
  label += count != null ? ": " : "";

  return tooltip == null ? (
    <LabelledValue label={label} value={count} />
  ) : (
    <Tooltip title={tooltip} placement={"right"}>
      <span>
        <LabelledValue label={label} value={count} />
      </span>
    </Tooltip>
  );
};

const StringSummary = ({ count, values, blanks, columnIndex }) => {
  const summary = summarizeStringValues({ count, blanks, values });
  return (
    <>
      <ColumnLabel index={columnIndex} />
      {summary.map(({ value, count, tooltip }) => (
        <StringValueCount
          key={value}
          value={value}
          count={count}
          tooltip={tooltip}
        />
      ))}
    </>
  );
};

const columnType = ({ numbers, dates, strings }) => {
  const numbersCount = numbers?.count ?? 0;
  const datesCount = dates?.count ?? 0;
  const stringsCount = strings?.count ?? 0;
  if (numbersCount + datesCount > stringsCount / 2) {
    return numbersCount > datesCount ? "numbers" : "dates";
  }
  return "strings";
};

const ColumnSummary = ({ name, columnIndex, summary, active }) => {
  const hasSummary = !utils.isEmptyObject(summary);
  const type = hasSummary ? columnType(summary) : null;

  let title = <ColumnLabel index={columnIndex} margin={0} />;

  if (type === "numbers") {
    title = <NumberSummary {...summary.numbers} columnIndex={columnIndex} />;
  } else if (type === "dates") {
    title = <DateSummary {...summary.dates} columnIndex={columnIndex} />;
  } else if (type === "strings") {
    title = (
      <StringSummary
        {...summary.strings}
        blanks={summary.blanks}
        columnIndex={columnIndex}
      />
    );
  }

  return (
    <Tooltip title={title} placement={"right"}>
      <Stack
        flexDirection={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        sx={{ width: "100%" }}
      >
        <Stack>
          <Typography variant={"subtitle1"}>{name}</Typography>
          {hasSummary && (
            <Typography variant={"caption"}>
              {summary.strings?.values?.[0]?.[0] ?? "-"}
            </Typography>
          )}
        </Stack>
        {active && <ActiveIcon />}
      </Stack>
    </Tooltip>
  );
};

const ColumnMappingHeader = ({
  title,
  mappedColumn,
  requiredMissing,
  tip,
  isLoading,
  onClick,
}) => {
  const theme = useTheme();
  const background = requiredMissing
    ? theme.brand.mapper.missing
    : mappedColumn == null
    ? theme.brand.mapper.unmapped
    : theme.brand.mapper.mapped;
  return (
    <TableCell
      sx={{
        padding: "4px 4px 4px 8px",
        verticalAlign: "top !important",
        borderRight: "1px solid gainsboro",
        background: background,
      }}
    >
      <Stack
        flexDirection={"row"}
        alignItems={"flex-start"}
        justifyContent={"space-between"}
      >
        <Stack>
          <Typography variant={"subtitle1"}>{title}</Typography>
          <Typography variant={"caption"}>
            {isLoading ? "-" : mappedColumn || "Unmapped"}
          </Typography>
        </Stack>
        <HeaderAction
          mappedColumn={mappedColumn}
          requiredMissing={requiredMissing}
          tip={tip}
          isLoading={isLoading}
          onClick={onClick}
        />
      </Stack>
    </TableCell>
  );
};

const FULFILLED = "fulfilled";

/**
 * Sheet Selector Component
 */
const SheetSelector = ({
  currentSheet,
  availableSheets,
  onSheetChange,
  sheetAnalysisResults,
  isAnalysisComplete,
}) => {
  const theme = useTheme();
  if (
    !availableSheets ||
    availableSheets.length === 0 ||
    (availableSheets.length <= 1 && isAnalysisComplete)
  ) {
    return null;
  }

  const getSheetDisplayName = (sheetName) => {
    const sheet = sheetAnalysisResults?.sheets?.find(
      (s) => s.sheet_name === sheetName
    );
    const isRecommended = sheetName === sheetAnalysisResults?.recommended_sheet;
    return { name: sheetName, confidence: sheet?.confidence, isRecommended };
  };

  const accent = theme.brand.accent[500];
  return (
    <Fade in timeout={500}>
      <Box
        sx={{
          mb: 3,
          p: 2,
          backgroundColor: `${accent}0D`,
          borderRadius: 2,
          border: `1px solid ${accent}33`,
        }}
      >
        <Typography variant={"h6"} color={"primary"} gutterBottom>
          {"Current Sheet"}
        </Typography>
        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          <FormControl sx={{ minWidth: 200 }}>
            <Select
              value={currentSheet}
              onChange={(e) => onSheetChange(e.target.value)}
              size={"small"}
              sx={{
                backgroundColor: theme.brand.white,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: `${accent}4D`,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: `${accent}80`,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: accent,
                },
              }}
            >
              {availableSheets.map((sheet) => {
                const { name, confidence, isRecommended } = getSheetDisplayName(
                  sheet
                );
                return (
                  <MenuItem key={sheet} value={sheet}>
                    <Stack
                      direction={"row"}
                      spacing={1}
                      alignItems={"center"}
                      width={"100%"}
                    >
                      <Typography variant={"body2"}>{name}</Typography>
                      {isRecommended && (
                        <Chip
                          label={"Recommended"}
                          size={"small"}
                          color={"primary"}
                          sx={{ fontSize: "0.65rem", height: "20px" }}
                        />
                      )}
                      {confidence && (
                        <Chip
                          label={`${Math.round(confidence * 100)}%`}
                          size={"small"}
                          variant={"outlined"}
                          sx={{
                            fontSize: "0.65rem",
                            height: "20px",
                            marginLeft: "auto",
                          }}
                        />
                      )}
                    </Stack>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <Typography variant={"body2"} color={"text.secondary"}>
            {`${availableSheets.length} sheet${
              availableSheets.length === 1 ? "" : "s"
            } available`}
            {!isAnalysisComplete && ` (loading others...)`}
          </Typography>
        </Stack>
      </Box>
    </Fade>
  );
};

/**
 * Clean, simplified ColumnMapper component
 */
const ColumnMapper = ({
  fileId,
  origin,
  tableName,
  headerRows,
  mapping,
  updateColumns,
  targetColumnList,
  setTargetColumnList,
  targetColumnMap,
  setTargetColumnMap,
  sourceHeaders,
  setSourceHeaders,
  outputColumns,
  tableSummary,
  parentRef,
  setTableName,
  setOrigin,
  setTableNameAndOrigin,
  sheetAnalysisResults,
  useSheetAnalysisWorkflow = false,
  analysisHeaders = null,
  isAnalysisComplete = false,
  // Multi-sheet props
  isMultiSheetMode = false,
  multiSheetConfig = null,
  currentSheetIndex = 0,
  setCurrentSheetIndex,
  selectedSheets = [],
}) => {
  const theme = useTheme();
  const {
    data: configuration,
    status: configurationStatus,
  } = staticDataService.useMultiConfigQuery(["claims_files", "translations"]);

  const request = {
    fileId,
    tableName,
    skipRows: origin?.row ?? 0,
    skipColumns: origin?.column ?? 0,
    headerRows: headerRows ?? 1,
    maxColumns: 300,
  };

  const skipQueries = !fileId || !tableName || origin == null;

  // Basic data queries
  const {
    data: sourceColumnsFromQuery,
    status: sourceColumnsStatus,
  } = useGetHeaderRowQuery(request, {
    skip: skipQueries || (useSheetAnalysisWorkflow && analysisHeaders),
  });

  const {
    data: columnTypes,
    status: columnTypesStatus,
  } = useGetColumnTypeSummaryQuery(request, {
    skip: skipQueries,
  });

  const { data: dataPage, status: dataPageStatus } = useGetTablePageQuery(
    {
      ...request,
      skipRows: request.skipRows + (request.headerRows || 1),
      maxRows: 100,
    },
    { skip: skipQueries }
  );

  const sourceColumns = useMemo(() => {
    if (useSheetAnalysisWorkflow && analysisHeaders) {
      return { header: analysisHeaders };
    }
    return sourceColumnsFromQuery;
  }, [useSheetAnalysisWorkflow, analysisHeaders, sourceColumnsFromQuery]);

  // State variables
  const [editColumn, setEditColumn] = useState(null);
  const [searchText, setSearchText] = useState(null);

  // Initialize target columns structure and headers
  useEffect(() => {
    if (
      configurationStatus === FULFILLED &&
      (sourceColumnsStatus === FULFILLED || sourceColumns?.header)
    ) {
      const notEqual = (listA, listB) => {
        if (listA?.length !== listB?.length) return true;
        if (listA === listB) return false;
        for (const i in listA) if (listA[i] !== listB[i]) return true;
        return false;
      };

      const headers = sourceColumns?.header ?? [];
      let headersChanged = notEqual(headers, sourceHeaders);
      if (headersChanged) {
        setSourceHeaders(headers);
      }

      const colNames = outputColumns.map((c) => c.column);
      const colNamesChanged = notEqual(targetColumnList, colNames);
      if (colNamesChanged) {
        setTargetColumnList(colNames);
      }
      if (colNamesChanged || headersChanged) {
        const targetMap = {};
        outputColumns.forEach((col) => {
          targetMap[col.column] = {
            column: col.column,
            label: col.display,
            required: col.required,
            tip: col.tip,
            suggestions: null,
            mappedColIndex: null,
            mappedColName: null,
          };
        });
        setTargetColumnMap(targetMap);
      }
    }
  }, [
    sourceColumns,
    sourceColumnsStatus,
    configuration,
    configurationStatus,
    fileId,
    tableName,
    targetColumnMap,
    targetColumnList,
    setTargetColumnList,
    setTargetColumnMap,
    sourceHeaders,
    setSourceHeaders,
    outputColumns,
  ]);

  // Load existing mappings when switching sheets (multi-sheet mode only)
  useEffect(() => {
    if (
      isMultiSheetMode &&
      mapping &&
      targetColumnMap &&
      sourceColumns?.header &&
      targetColumnList?.length > 0
    ) {
      const headers = sourceColumns.header;
      const updatedTargetMap = { ...targetColumnMap };
      let hasChanges = false;

      // Reset all mappings first
      Object.keys(updatedTargetMap).forEach((col) => {
        if (updatedTargetMap[col].mappedColIndex !== null) {
          updatedTargetMap[col].mappedColIndex = null;
          updatedTargetMap[col].mappedColName = null;
          hasChanges = true;
        }
      });

      // Load existing mappings from the current sheet
      mapping.forEach((mappingEntry) => {
        const { outputName, inputColumn } = mappingEntry;
        if (
          updatedTargetMap[outputName] &&
          headers[inputColumn] !== undefined
        ) {
          updatedTargetMap[outputName].mappedColIndex = inputColumn;
          updatedTargetMap[outputName].mappedColName = headers[inputColumn];
          hasChanges = true;
        }
      });

      if (hasChanges) {
        setTargetColumnMap(updatedTargetMap);
      }
    }
  }, [
    isMultiSheetMode,
    currentSheetIndex,
    tableName,
    mapping,
    sourceColumns,
    targetColumnList,
    targetColumnMap,
    setTargetColumnMap,
  ]);

  // Get feature flags from hardcoded staticDataService
  const useAiAgentsColumnMapping = staticDataService.useFeatureFlag(
    USE_AI_AGENTS_COLUMN_MAPPING,
    { default: false }
  );

  // Column mapping suggestions queries
  const legacyQuery = useGetColumnMappingSuggestionsQuery(columnTypes?.types, {
    skip:
      !columnTypes ||
      columnTypesStatus !== FULFILLED ||
      useAiAgentsColumnMapping ||
      useSheetAnalysisWorkflow,
  });

  const aiQuery = useGetColumnMappingSuggestionsAIQuery(
    {
      columnTypes: columnTypes?.types,
      fileId,
      dataPage: dataPage?.page,
      headers: sourceColumns?.header,
    },
    {
      skip:
        !columnTypes ||
        columnTypesStatus !== FULFILLED ||
        !useAiAgentsColumnMapping ||
        !dataPage ||
        !sourceColumns ||
        useSheetAnalysisWorkflow,
    }
  );

  // Use the appropriate result based on the feature flag
  const {
    data: suggestions,
    status: suggestionsStatus,
  } = useAiAgentsColumnMapping ? aiQuery : legacyQuery;

  const columnNameMapping = (targetName, sourceColumn) => {
    return {
      outputName: targetName,
      inputColumn: sourceColumn,
      conversionType: ["_OPEN_CLOSED", "_COVERAGE"].includes(targetName)
        ? "CONVERT"
        : ["_EXCLUDE"].includes(targetName)
        ? "IN_DELETE_ROW"
        : "",
    };
  };

  // Apply suggestions
  useEffect(() => {
    // Skip traditional suggestions if using AI workflow
    if (useSheetAnalysisWorkflow) {
      return;
    }

    const suggestionsList = suggestions?.suggestions;
    if (
      suggestionsStatus === FULFILLED &&
      (sourceColumnsStatus === FULFILLED || sourceColumns?.header)
    ) {
      let changed = false;
      const init = { ...targetColumnMap };
      const columnUpdates = [];

      for (const column in targetColumnMap) {
        const suggest = suggestionsList[column];
        if (!!suggest && !init[column].suggestions) {
          init[column] = {
            ...targetColumnMap[column],
            suggestions: suggest,
          };
          if (
            init[column].mappedColIndex == null &&
            (suggest?.length || 0) > 0 &&
            suggest[0].confidence > 0.1
          ) {
            const suggestedIndex = sourceColumns?.header.indexOf(
              suggest[0].suggestion
            );
            init[column].mappedColIndex = suggestedIndex;
            init[column].mappedColName = suggest[0].suggestion;
            columnUpdates.push(columnNameMapping(column, suggestedIndex));
          }
          changed = true;
        }
      }

      if (changed) {
        setTargetColumnMap(init);
        updateColumns(columnUpdates);
      }
    }
  }, [
    useSheetAnalysisWorkflow,
    suggestionsStatus,
    suggestions,
    targetColumnMap,
    sourceColumns,
    sourceColumnsStatus,
    updateColumns,
    setTargetColumnMap,
  ]);

  // Apply sheet analysis suggestions (for AI workflow)
  useEffect(() => {
    // Guard against running without the necessary data
    if (
      !useSheetAnalysisWorkflow ||
      !sheetAnalysisResults ||
      !tableName ||
      !sourceColumns?.header ||
      Object.keys(targetColumnMap).length === 0
    ) {
      return;
    }

    // Prevents re-applying suggestions in a loop
    const alreadyApplied = Object.values(targetColumnMap).some(
      (col) => col.suggestions
    );
    if (alreadyApplied) {
      return;
    }

    // Find the current sheet in the analysis results
    const currentSheet = sheetAnalysisResults.sheets?.find(
      (sheet) => sheet.sheet_name === tableName
    );

    if (!currentSheet?.column_mappings?.length) {
      return; // No mappings for this sheet
    }

    // Convert to suggestions format
    const suggestions = {};
    currentSheet.column_mappings.forEach((mapping) => {
      if (!suggestions[mapping.target_mapping]) {
        suggestions[mapping.target_mapping] = [];
      }
      suggestions[mapping.target_mapping].push({
        suggestion: mapping.column_name,
        confidence: mapping.confidence,
        reasoning: mapping.reasoning,
      });
    });

    // Apply suggestions and mappings
    const updatedTargetMap = { ...targetColumnMap };
    const columnUpdates = [];
    const currentHeaders = sourceColumns.header;

    Object.keys(updatedTargetMap).forEach((targetColumn) => {
      const suggest = suggestions[targetColumn];
      if (suggest && suggest.length > 0) {
        updatedTargetMap[targetColumn] = {
          ...updatedTargetMap[targetColumn],
          suggestions: suggest,
        };

        // Auto-apply high confidence mappings
        if (suggest[0].confidence > 0.1) {
          const suggestedIndex = currentHeaders.indexOf(suggest[0].suggestion);

          if (suggestedIndex >= 0) {
            updatedTargetMap[targetColumn].mappedColIndex = suggestedIndex;
            updatedTargetMap[targetColumn].mappedColName =
              suggest[0].suggestion;

            columnUpdates.push({
              outputName: targetColumn,
              inputColumn: suggestedIndex,
              conversionType: ["_OPEN_CLOSED", "_COVERAGE"].includes(
                targetColumn
              )
                ? "CONVERT"
                : ["_EXCLUDE"].includes(targetColumn)
                ? "IN_DELETE_ROW"
                : "",
            });
          }
        }
      }
    });

    if (columnUpdates.length > 0) {
      setTargetColumnMap(updatedTargetMap);
      updateColumns(columnUpdates);
    }
  }, [
    useSheetAnalysisWorkflow,
    sheetAnalysisResults,
    tableName,
    sourceColumns,
    targetColumnMap,
    setTargetColumnMap,
    updateColumns,
  ]);

  useEffect(() => {
    if (editColumn != null && parentRef.current) {
      parentRef.current.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [editColumn, parentRef]);

  const updateMapping = (index, name) => {
    if (editColumn != null) {
      const currentTargetList = targetColumnList || [];
      const currentTargetMap = targetColumnMap || {};
      const targetColumnName = currentTargetList[editColumn];
      const colEntry = currentTargetMap[targetColumnName];

      if (colEntry && colEntry.mappedColIndex !== index) {
        const updatedTargetColumns = {
          ...currentTargetMap,
          [targetColumnName]: {
            ...currentTargetMap[targetColumnName],
            mappedColIndex: index,
            mappedColName: name,
          },
        };

        setTargetColumnMap(updatedTargetColumns);
        updateColumns([columnNameMapping(targetColumnName, index)]);
      }
      setEditColumn(null);
    }
  };

  // Show loader until all required data is loaded
  if (
    (!useSheetAnalysisWorkflow && suggestionsStatus !== FULFILLED) ||
    (sourceColumnsStatus !== FULFILLED && !sourceColumns?.header) ||
    dataPageStatus !== FULFILLED
  ) {
    return <CentredLoader />;
  }

  // Show sheet selector immediately if we have analysis results
  const showSheetSelector =
    useSheetAnalysisWorkflow && sheetAnalysisResults && !isMultiSheetMode;

  // Handle sheet changes
  const handleSheetChange = (newSheet) => {
    if (newSheet !== tableName && useSheetAnalysisWorkflow) {
      // Clear current mappings
      const clearedTargetMap = {};
      Object.keys(targetColumnMap || {}).forEach((col) => {
        clearedTargetMap[col] = {
          ...(targetColumnMap[col] || {}),
          mappedColIndex: null,
          mappedColName: null,
          suggestions: null,
        };
      });

      setTargetColumnMap(clearedTargetMap);
      updateColumns([]);

      // Get new sheet positioning
      const newSheetData = sheetAnalysisResults?.sheets?.find(
        (s) => s.sheet_name === newSheet
      );
      let newOrigin = { row: 0, column: 0 };

      if (newSheetData?.table_start) {
        const row = Math.max(0, (newSheetData.table_start.row || 1) - 1);
        const column = Math.max(0, (newSheetData.table_start.column || 1) - 1);
        newOrigin = { row, column };
      }

      // Update sheet and origin
      if (setTableNameAndOrigin) {
        setTableNameAndOrigin(newSheet, newOrigin);
      } else {
        setTableName(newSheet);
        setOrigin(newOrigin);
      }
    }
  };

  const page = dataPage?.page || [];
  const hasSearchText = (searchText ?? "").trim() !== "";
  const editColumns = (sourceColumns?.header ?? [])
    .map((col, index) => {
      return { name: col, position: index };
    })
    .filter(
      (col) =>
        !hasSearchText ||
        col.name.toLowerCase().includes(searchText.trim().toLowerCase())
    );

  const toggleEditColumn = (newColumn) => {
    setSearchText(null);
    setEditColumn(newColumn === editColumn ? null : newColumn);
  };

  return (
    <>
      {/* Multi-sheet tabs - simplified */}
      {isMultiSheetMode && selectedSheets.length > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={currentSheetIndex}
            onChange={(e, newValue) => setCurrentSheetIndex(newValue)}
          >
            {selectedSheets.map((sheetName, index) => (
              <Tab key={index} label={sheetName} />
            ))}
          </Tabs>
        </Box>
      )}

      {showSheetSelector && (
        <SheetSelector
          currentSheet={tableName}
          availableSheets={
            sheetAnalysisResults?.sheets?.map((s) => s.sheet_name) || []
          }
          onSheetChange={handleSheetChange}
          sheetAnalysisResults={sheetAnalysisResults}
          isAnalysisComplete={isAnalysisComplete}
        />
      )}

      <TableContainer sx={{ overflowX: "initial" }}>
        <Table
          stickyHeader
          sx={{
            background: theme.brand.white,
            borderTop: "1px solid gainsboro",
            borderLeft: "1px solid gainsboro",
          }}
        >
          <TableHead>
            <TableRow>
              {targetColumnList.map((targetColName, targetColIndex) => {
                const targetColumn = targetColumnMap[targetColName];
                return (
                  <ColumnMappingHeader
                    key={targetColIndex}
                    title={targetColumn.label}
                    mappedColumn={
                      targetColumn.mappedColIndex == null
                        ? null
                        : sourceColumns.header?.[targetColumn.mappedColIndex]
                    }
                    requiredMissing={
                      targetColumn.mappedColIndex == null &&
                      targetColumn.required
                    }
                    tip={targetColumn.tip}
                    isLoading={false}
                    onClick={() => toggleEditColumn(targetColIndex)}
                  />
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {page.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {targetColumnList.map((colName, colIndex) => {
                  const colEntry = targetColumnMap?.[colName] || {};
                  const editColEntry =
                    editColumn == null
                      ? {}
                      : targetColumnMap?.[targetColumnList[editColumn]];
                  return editColumn != null &&
                    editColumn === colIndex &&
                    rowIndex !== 0 ? (
                    <Fragment key={colIndex}></Fragment>
                  ) : editColumn === colIndex && rowIndex === 0 ? (
                    <TableCell
                      key={colIndex}
                      rowSpan={page.length}
                      sx={{
                        verticalAlign: "top",
                        borderRight: "1px solid gainsboro",
                      }}
                      className={"fade-in"}
                    >
                      <Stack>
                        <InlineSearch
                          placeholder={"Filter spreadsheet columns"}
                          onChange={(searchText) => setSearchText(searchText)}
                          fullWidth
                        />
                        <List sx={{ width: "100%" }}>
                          {editColumns.map((col) => (
                            <ListItem key={col.position} sx={{ padding: 0 }}>
                              <ListItemButton
                                onClick={() =>
                                  updateMapping(col.position, col.name)
                                }
                              >
                                <ColumnSummary
                                  name={col.name}
                                  columnIndex={
                                    col.position + (origin?.column ?? 0)
                                  }
                                  summary={tableSummary[col.position] ?? {}}
                                  active={
                                    col.position === editColEntry.mappedColIndex
                                  }
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                          {hasSearchText && editColumns.length === 0 && (
                            <Typography
                              margin={2}
                              color={"dimgray"}
                              align={"center"}
                            >
                              {"No matches for '"}
                              {searchText.trim()}
                              {"'"}
                            </Typography>
                          )}
                        </List>
                        <Stack gap={1}>
                          <Button
                            sx={{ width: "100%" }}
                            onClick={() => setEditColumn(null)}
                          >
                            {"Done"}
                          </Button>
                          <Button
                            sx={{ width: "100%" }}
                            onClick={() => updateMapping(null, null)}
                            color={"secondary"}
                          >
                            {"Clear Mapping"}
                          </Button>
                        </Stack>
                      </Stack>
                    </TableCell>
                  ) : (
                    <TableCell
                      sx={{ borderRight: "1px solid gainsboro" }}
                      key={"display-cell-" + rowIndex + "-" + colIndex}
                    >
                      {colEntry.mappedColIndex == null
                        ? ""
                        : row[colEntry.mappedColIndex]}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export const summarizeStringValues = ({ count, blanks, values }) => {
  if (count === 0) {
    return [{ value: blanks > 0 ? "All Blank" : "No values" }];
  }
  if (values.length === 1) {
    return [{ value: `All (${values[0][1]}): ${values[0][0]}` }];
  }
  let multi = values
    .filter(([v, c]) => c > 1)
    .map(([value, count]) => ({
      value: value.length > 24 ? `${value.slice(0, 24)}…` : value,
      tooltip: value.length > 24 ? value : undefined,
      count,
    }))
    .slice(0, 5);
  let various = count - multi.reduce((acc, cur) => (acc += cur.count), 0);
  if (various > 0) {
    multi = multi.slice(0, 4);
  }
  various = count - multi.reduce((acc, cur) => (acc += cur.count), 0);
  return [
    ...multi,
    ...(various === 0 ? [] : [{ value: "Various", count: various }]),
  ];
};

export default ColumnMapper;
